import {
    SubscriptionPaymentMethod,
    type SubscriptionOrderSummary,
} from '@houzy/schemas/partner-management/partner-subscription-management';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { PAYMENT_METHOD_PRESENTATION } from '../../../lib/paymentMethodPresentation';
import { stateColor } from '../../../theme';
import { CheckoutSectionCard } from './CheckoutSectionCard';

export type CheckoutPaymentMethodCardProps = {
    offer: SubscriptionOrderSummary;
    selectableMethods?: SubscriptionPaymentMethod[];
    onMethodChange?: (method: SubscriptionPaymentMethod) => void;
    // Rendered under the tabs while the invoice method is selected.
    invoiceDetails?: ReactNode;
};

const PaymentRoute = { Stripe: 'Stripe', Invoice: 'Invoice' } as const;

type PaymentRoute = (typeof PaymentRoute)[keyof typeof PaymentRoute];

const routeOf = (method: SubscriptionPaymentMethod): PaymentRoute =>
    method === SubscriptionPaymentMethod.Invoice ? PaymentRoute.Invoice : PaymentRoute.Stripe;

export function CheckoutPaymentMethodCard({
    offer,
    selectableMethods,
    onMethodChange,
    invoiceDetails,
}: CheckoutPaymentMethodCardProps): ReactElement {
    const { t } = useTranslation();
    const { icon, copy } = PAYMENT_METHOD_PRESENTATION[offer.paymentMethod];

    const stripeMethod = selectableMethods?.includes(SubscriptionPaymentMethod.Invoice)
        ? selectableMethods.find((method) => method !== SubscriptionPaymentMethod.Invoice)
        : undefined;
    const selectedRoute = routeOf(offer.paymentMethod);

    const chooseRoute = (route: PaymentRoute): void => {
        if (!stripeMethod) {
            return;
        }
        onMethodChange?.(route === PaymentRoute.Invoice ? SubscriptionPaymentMethod.Invoice : stripeMethod);
    };

    return (
        <CheckoutSectionCard title={t('CHECKOUT.PAYMENT.TITLE')}>
            <Stack spacing={2}>
                {stripeMethod ? (
                    <Tabs
                        value={selectedRoute}
                        onChange={(_event, route: PaymentRoute) => chooseRoute(route)}
                        variant="fullWidth"
                        aria-label={t('CHECKOUT.PAYMENT.TITLE')}>
                        <Tab
                            value={PaymentRoute.Stripe}
                            label={t('CHECKOUT.PAYMENT.CARD_OR_TWINT')}
                            icon={PAYMENT_METHOD_PRESENTATION[SubscriptionPaymentMethod.Card].icon}
                            iconPosition="start"
                            sx={{ gap: 1 }}
                        />
                        <Tab
                            value={PaymentRoute.Invoice}
                            label={t('CHECKOUT.PAYMENT.INVOICE')}
                            icon={PAYMENT_METHOD_PRESENTATION[SubscriptionPaymentMethod.Invoice].icon}
                            iconPosition="start"
                            sx={{ gap: 1 }}
                        />
                    </Tabs>
                ) : (
                    <Paper
                        variant="outlined"
                        sx={{
                            bgcolor: 'background.default',
                            px: 2,
                            py: 0.75,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                        }}>
                        <Avatar
                            sx={{
                                width: 40,
                                height: 40,
                                bgcolor: (theme) => stateColor(theme, 'focus'),
                                color: 'primary.main',
                            }}>
                            {icon}
                        </Avatar>
                        <Stack sx={{ minWidth: 0, py: 1 }}>
                            <Typography variant="subtitle2">{t(`CHECKOUT.PAYMENT.${copy}`)}</Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 500, fontSize: 11, letterSpacing: '0.3px' }}>
                                {t(`CHECKOUT.PAYMENT.${copy}_HINT`)}
                            </Typography>
                        </Stack>
                    </Paper>
                )}

                {selectedRoute === PaymentRoute.Invoice && invoiceDetails}

                {offer.billingAddress && !invoiceDetails && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Stack direction="row" spacing={1}>
                            <PlaceOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            <Stack sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle2">{t('CHECKOUT.PAYMENT.BILLING_ADDRESS')}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {offer.billingAddress.companyName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {offer.billingAddress.line1}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {offer.billingAddress.zip} {offer.billingAddress.city}
                                </Typography>
                            </Stack>
                        </Stack>
                    </Paper>
                )}

                <Alert severity="info" icon={<InfoOutlinedIcon fontSize="inherit" />}>
                    {t(`CHECKOUT.PAYMENT.${copy}_NOTICE`)}
                </Alert>
            </Stack>
        </CheckoutSectionCard>
    );
}
